// Angular
import { inject } from '@angular/core';
import { ResolveFn, Router } from '@angular/router';

// RxJS
import { Observable } from 'rxjs';
import { catchError, map, switchMap, take } from 'rxjs/operators';

// Data Contracts
import {
  ListResponseOfUserDetails,
  ListResponseOfUserItem,
  OrganizationInformation,
} from '@data-contracts';

// Local
import { OrganizationsApiService } from '@app/services/api/organizations/organizations-api.services';
import { IdentityClaimsService } from '@app/services/identity-claims/identity-claims.service';
import { ErrorsStateService } from '@app/services/ui-state/errors-state/errors-state.service';
import { EmulationService } from '@core/auth/emulation.service';
import { ROUTES } from '@routes';

/**
 * The effective organization id, emitted once any persisted emulation has been restored. Without the
 * ensureReady() gate a refresh resolves the admin's org id, because the emulation overlay is applied
 * asynchronously after the real identity claims are set.
 *
 * Must be invoked inside a resolver (or other injection context) — it calls inject().
 */
const effectiveOrgId$ = (): Observable<string> => {
  const emulationService = inject(EmulationService);
  const identityClaimsService = inject(IdentityClaimsService);

  return emulationService.ensureReady().pipe(
    switchMap(() => identityClaimsService.orgId$),
    take(1),
  );
};

/**
 * Whether the effective user is a Form 50 Agent or self-represented individual. Waits for identity
 * claims and any persisted emulation, so the answer reflects the emulated user.
 *
 * Must be invoked inside a resolver (or other injection context) — it calls inject().
 */
const isAccountSettingsUser$ = (): Observable<boolean> => {
  const identityClaimsService = inject(IdentityClaimsService);

  return effectiveOrgId$().pipe(
    map(() => identityClaimsService.isAccountSettingsUser()),
  );
};

/**
 * Resolver for whether the effective user is a Form 50 Agent or self-represented individual.
 */
export const isAccountSettingsUserResolver: ResolveFn<
  Observable<boolean>
> = () => isAccountSettingsUser$();

/**
 * Browser tab title for the My Organization route. Form 50 Agents and self-represented individuals
 * see their own account, so their tab reads My User. Emulation-aware.
 */
export const myOrganizationTitleResolver: ResolveFn<string> = () =>
  isAccountSettingsUser$().pipe(
    map(isAccountSettingsUser =>
      isAccountSettingsUser
        ? 'Preferences | My User'
        : 'Preferences | My Organization',
    ),
  );

/**
 * Resolver to get organization users (emulation-aware org id).
 */
export const organizationUsersResolver: ResolveFn<
  Observable<ListResponseOfUserItem | boolean>
> = () => {
  const organizationsApiService = inject(OrganizationsApiService);
  const errorsStateService = inject(ErrorsStateService);
  const router = inject(Router);

  return effectiveOrgId$().pipe(
    switchMap(orgId => organizationsApiService.getUsers(orgId)),
    catchError(err => {
      console.error(
        'An error occurred while fetching organization users.',
        err,
      );
      errorsStateService.showError(
        'An error occurred while fetching organization users.',
      );
      return router.navigate([ROUTES.HOME]);
    }),
  );
};

/**
 * Resolver to get organization information (emulation-aware org id).
 */
export const organizationInfoResolver: ResolveFn<
  Observable<OrganizationInformation | boolean>
> = () => {
  const organizationsApiService = inject(OrganizationsApiService);
  const errorsStateService = inject(ErrorsStateService);
  const router = inject(Router);

  return effectiveOrgId$().pipe(
    switchMap(orgId => organizationsApiService.getOrg(orgId)),
    catchError(err => {
      console.error('An error occurred while fetching organization info.', err);
      errorsStateService.showError(
        'An error occurred while fetching organization info.',
      );
      return router.navigate([ROUTES.HOME]);
    }),
  );
};

/**
 * Resolver to get organization management users (emulation-aware org id).
 */
export const manageUserResolver: ResolveFn<
  Observable<ListResponseOfUserDetails | boolean>
> = () => {
  const organizationsApiService = inject(OrganizationsApiService);
  const errorsStateService = inject(ErrorsStateService);
  const router = inject(Router);

  return effectiveOrgId$().pipe(
    switchMap(orgId => organizationsApiService.getManagementUsers(orgId)),
    catchError(err => {
      console.error(
        'An error occurred while fetching organization users.',
        err,
      );
      errorsStateService.showError(
        'An error occurred while fetching organization users.',
      );
      return router.navigate([ROUTES.HOME]);
    }),
  );
};
